import { Button } from '@zillow/constellation-10';

import {
  ToursSummary,
  type ToursSummaryDayChipType,
  type ToursSummaryTileType,
  useHasFutureTourAvailability,
  useListingTourConfig,
  useTouringTreatmentForZuidAndListing,
  useUpcomingToursData,
} from '@zg-rentals/touring';

import { RENTAL_TOUR_ILLUSTRATION_URL } from '../../../../constants/DigitalLibraryConstants';
import { getTourCalendarRoute } from '../../../../constants/urls';

import { useAuth } from '../../../../components/AuthProvider';
import EmptyState from '../../../../components/EmptyState';
import SpotIllustration from '../../../../components/EmptyState/SpotIllustration';

import {
  emitTourSchedulingUpsellSetTourAvailabilityCtaEvent,
  emitToursSummaryDayChipClickEvent,
  emitToursSummaryImpressionEvent,
  emitToursSummaryTileClickEvent,
  emitToursSummaryTourCardClickEvent,
  emitUpcomingToursMoreToursCtaEvent,
  TOUR_SCHEDULING_UPSELL_SET_TOUR_AVAILABILITY_CTA_EVENT_ID,
  UPCOMING_TOURS_MORE_TOURS_CTA_EVENT_ID,
} from '../../../../analytics/events/overviewPage';
import ConnectedModuleCard from '../../../ConnectedModuleCard';

type Props = {
  listingAlias: string;
  isActive: boolean;
  inquiriesPath: string;
};

const UpcomingToursModule = ({ listingAlias, isActive, inquiriesPath }: Props) => {
  const { user } = useAuth();

  const { pathname: tourCalendarPathname } = getTourCalendarRoute({ listingAlias });
  const tourCalendarPath = `/rental-manager${tourCalendarPathname.replace('[listingAlias]', listingAlias)}`;

  const { data: touringTreatment, isLoading: isGetTouringTreatmentLoading } = useTouringTreatmentForZuidAndListing(
    { listingAlias },
    { enabled: Boolean(listingAlias), errorDetails: 'UpcomingToursModule - Error fetching touring treatment' },
  );

  const isTouringEnabled = Boolean(touringTreatment?.isTouringEnabledForListing);
  const hasBookedTours = Boolean(touringTreatment?.hasBookedTours);

  // Shares a query key with the widget's own call, so this gate costs no extra request.
  const { hasTourConfig } = useListingTourConfig(listingAlias);

  // A slow/errored config or treatment keeps the card rather than flashing it in and out as they settle.
  const canRenderCard = (isActive || hasBookedTours) && hasTourConfig !== false;

  // Extra request (no shared key); skip when a booked tour already short-circuits the empty-state check.
  const {
    hasFutureAvailability,
    isError: isAvailabilityError,
    isPending: isAvailabilityPending,
  } = useHasFutureTourAvailability(listingAlias, {
    enabled: isTouringEnabled && canRenderCard && !hasBookedTours,
  });

  // Also shares a query key with the widget's own call — used only to decide the empty state here.
  const {
    counts,
    isError: isToursDataError,
    isPending: isToursDataPending,
  } = useUpcomingToursData(listingAlias, {
    enabled: isTouringEnabled && canRenderCard,
  });

  const onCalendarClick = () => {
    if (user) {
      emitUpcomingToursMoreToursCtaEvent({ currentProperty: { listingAlias }, user });
    }
  };

  const handleToursSummaryImpression = () => {
    if (user) {
      emitToursSummaryImpressionEvent({ currentProperty: { listingAlias }, user });
    }
  };

  const handleToursSummaryTileClick = (tile: ToursSummaryTileType) => {
    if (user) {
      emitToursSummaryTileClickEvent({ currentProperty: { listingAlias }, user, tile });
    }
  };

  const handleToursSummaryDayChipClick = (chip: ToursSummaryDayChipType) => {
    if (user) {
      emitToursSummaryDayChipClickEvent({ currentProperty: { listingAlias }, user, chip });
    }
  };

  const handleToursSummaryTourCardClick = () => {
    if (user) {
      emitToursSummaryTourCardClickEvent({ currentProperty: { listingAlias }, user });
    }
  };

  const handleTourSchedulingUpsellClick = () => {
    if (user) {
      emitTourSchedulingUpsellSetTourAvailabilityCtaEvent({ currentProperty: { listingAlias }, user });
    }
  };

  // Touring being off for the listing sends the tour calendar to tour-availability, which drops the
  // deep-linked tourId, so the card stays hidden there.
  const isTouringOffForListing = Boolean(touringTreatment) && !isTouringEnabled;

  // Only a confirmed negative hides the card; a slow/errored gate leaves it in place.
  if (!canRenderCard || isTouringOffForListing) {
    return null;
  }

  // Canceled tours are history, not something to act on, so a listing left with only cancellations
  // still gets the set-availability prompt rather than a widget of dead rows.
  const hasAnyTourEvents = counts.requested > 0 || counts.booked > 0;
  // isPending rather than isLoading: a paused query (e.g. offline) is not fetching but has no data
  // either, and must not read as a settled zero.
  const areGateQueriesSettled =
    !isAvailabilityPending && !isAvailabilityError && !isToursDataPending && !isToursDataError;
  // All conditions must be confirmed positives: a pending config could resolve to false and unmount the card.
  const isConfirmedEmpty =
    isTouringEnabled &&
    hasTourConfig === true &&
    areGateQueriesSettled &&
    !hasBookedTours &&
    !hasAnyTourEvents &&
    !hasFutureAvailability;

  // The treatment and availability gates settle after the widget's own queries, so a settled widget
  // could still unmount (touring off) or swap to the empty state; hold the impression until it can't.
  const couldStillBeReplaced =
    isGetTouringTreatmentLoading ||
    (isTouringEnabled &&
      hasTourConfig === true &&
      !hasBookedTours &&
      !hasAnyTourEvents &&
      !hasFutureAvailability &&
      isAvailabilityPending);

  // Matches the redirect predicate in tour-calendar.tsx; errored treatment fails closed.
  const canDeepLinkToCalendar =
    isGetTouringTreatmentLoading || hasBookedTours || Boolean(touringTreatment?.hasAvailability);

  return (
    <ConnectedModuleCard css={{ borderRadius: 'obj.default' }}>
      {isConfirmedEmpty ? (
        // Touring is on, but with nothing to show and no way to book, prompt the landlord to set
        // availability instead of rendering an empty-looking widget.
        <EmptyState
          data-testid="EmptyUpcomingToursCard"
          icon={<SpotIllustration src={RENTAL_TOUR_ILLUSTRATION_URL} />}
          heading="No tours scheduled yet"
          body="Set your tour availability so renters can book a tour."
          align="left"
          useCard={false}
          actions={
            <Button tone="brand" emphasis="filled" asChild>
              <a
                href={tourCalendarPath}
                onClick={handleTourSchedulingUpsellClick}
                data-eventid={TOUR_SCHEDULING_UPSELL_SET_TOUR_AVAILABILITY_CTA_EVENT_ID}
              >
                Set tour availability
              </a>
            </Button>
          }
        />
      ) : (
        <ToursSummary
          listingAlias={listingAlias}
          tourCalendarPath={tourCalendarPath}
          inquiriesPath={inquiriesPath}
          canDeepLinkToCalendar={canDeepLinkToCalendar}
          onImpression={user && !couldStillBeReplaced ? handleToursSummaryImpression : undefined}
          onViewCalendarClick={onCalendarClick}
          onTileClick={handleToursSummaryTileClick}
          onDayChipClick={handleToursSummaryDayChipClick}
          onTourCardClick={handleToursSummaryTourCardClick}
          viewCalendarEventId={UPCOMING_TOURS_MORE_TOURS_CTA_EVENT_ID}
        />
      )}
    </ConnectedModuleCard>
  );
};

export default UpcomingToursModule;
